<template>
  <div class="olj-thread-container flex flex-col h-full bg-card">
    <!-- Loading State -->
    <div v-if="loading" class="flex-1 flex items-center justify-center p-12">
      <div class="flex flex-col items-center">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
        ></div>
        <p class="mt-4 text-sm text-muted-foreground">Loading thread details...</p>
      </div>
    </div>

    <!-- Thread Content -->
    <div v-else-if="threadData" class="flex-1 flex flex-col overflow-hidden">
      <!-- Thread Header -->
      <div class="thread-header border-b border-border p-4 bg-card">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 flex items-center justify-center text-lg font-bold text-white"
            >
              {{
                partnerName
                  ? partnerName.charAt(0).toUpperCase()
                  : "O"
              }}
            </div>
            <div>
              <h2 class="font-semibold text-foreground">
                {{ partnerName || "Employer" }}
              </h2>
              <p class="text-sm text-muted-foreground">
                {{ threadData.thread?.subject || "No subject" }}
              </p>
            </div>
          </div>
          <a
            :href="`https://v2.onlinejobs.ph/message/conversation/${hashId}`"
            target="_blank"
            class="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            Open Conversation
          </a>
        </div>
      </div>

      <!-- Messages Container -->
      <div
        class="messages-container flex-1 overflow-y-auto p-4 bg-muted/30"
        ref="messagesContainer"
      >
        <div v-if="messages.length === 0" class="text-center py-12">
          <p class="text-muted-foreground">No messages found in this thread</p>
        </div>
        <div v-else class="space-y-4">
          <div
            v-for="(message, index) in messages"
            :key="message.id || index"
            class="message-item"
            :class="{
              'message-from-user': isMessageFromUser(message),
              'message-from-partner': !isMessageFromUser(message),
            }"
          >
            <div
              class="message-bubble p-4 rounded-lg max-w-[70%]"
              :class="{
                'bg-primary text-primary-foreground': isMessageFromUser(message),
                'bg-muted text-foreground border border-border':
                  !isMessageFromUser(message),
              }"
            >
              <div class="message-header mb-2 flex items-center justify-between">
                <span
                  class="text-xs font-medium opacity-90"
                  :class="{
                    'text-white': isMessageFromUser(message),
                    'text-muted-foreground': !isMessageFromUser(message),
                  }"
                >
                  {{
                    isMessageFromUser(message)
                      ? "You"
                      : partnerName || "Employer"
                  }}
                </span>
                <span
                  class="text-xs opacity-75 ml-2"
                  :class="{
                    'text-white': isMessageFromUser(message),
                    'text-muted-foreground': !isMessageFromUser(message),
                  }"
                >
                  {{ formatMessageDate(message.created_at || message.sent_at) }}
                </span>
              </div>
              <div
                class="message-body"
                :class="{
                  'text-white': isMessageFromUser(message),
                  'text-foreground': !isMessageFromUser(message),
                }"
                v-html="formatMessageBody(message)"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="flex-1 flex items-center justify-center p-12">
      <div class="text-center">
        <template v-if="errorType === 'not_found'">
          <p class="text-muted-foreground mb-1">This conversation isn't available right now.</p>
          <p class="text-sm text-muted-foreground mb-4">It may have been deleted or is not accessible with your current connection.</p>
          <a
            :href="`https://v2.onlinejobs.ph/message/conversation/${hashId}`"
            target="_blank"
            class="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark transition-colors inline-flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open Conversation
          </a>
        </template>
        <template v-else>
          <p class="text-muted-foreground mb-4">Failed to load thread details</p>
          <button
            @click="$emit('retry')"
            class="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark transition-colors"
          >
            Retry
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import DOMPurify from "dompurify";
import authStore from "@/store/auth";

export default {
  name: "OljThreadDisplay",
  props: {
    threadData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hashId: {
      type: String,
      required: true,
    },
    partnerData: {
      type: Object,
      default: null,
    },
    errorType: {
      type: String,
      default: null,
    },
  },
  emits: ["retry"],
  setup(props) {
    const messagesContainer = ref(null);

    const partnerName = computed(() => {
      // First try to get from threadData
      if (props.threadData) {
        const name =
          props.threadData.partner?.name ||
          props.threadData.partner?.business_name ||
          props.threadData.partner?.contact_name;
        if (name) return name;
      }
      // Fallback to partnerData prop if threadData doesn't have it
      if (props.partnerData) {
        return (
          props.partnerData.name ||
          props.partnerData.business_name ||
          props.partnerData.contact_name ||
          null
        );
      }
      return null;
    });

    const messages = computed(() => {
      if (!props.threadData) return [];
      let messageArray = [];
      
      // Handle different possible response structures
      if (Array.isArray(props.threadData.data)) {
        messageArray = [...props.threadData.data]; // Create a copy to avoid mutating original
      } else if (props.threadData.messages && Array.isArray(props.threadData.messages)) {
        messageArray = [...props.threadData.messages];
      } else if (props.threadData.data?.data && Array.isArray(props.threadData.data.data)) {
        messageArray = [...props.threadData.data.data];
      }
      
      // The API returns messages with newest first, so we need to reverse to show
      // oldest first (at top) and newest last (at bottom) - like a normal chat
      messageArray = messageArray.reverse();
      
      // Sort messages by date in ascending order (oldest first, newest last) as backup
      // in case the API order isn't consistent
      return messageArray.sort((a, b) => {
        const dateA = a.created_at || a.sent_at || a.date || a.timestamp || 0;
        const dateB = b.created_at || b.sent_at || b.date || b.timestamp || 0;
        
        // Convert to timestamps for comparison
        const timestampA = dateA ? new Date(dateA).getTime() : 0;
        const timestampB = dateB ? new Date(dateB).getTime() : 0;
        
        // Handle invalid dates
        const validA = !isNaN(timestampA) && timestampA > 0;
        const validB = !isNaN(timestampB) && timestampB > 0;
        
        if (!validA && !validB) return 0;
        if (!validA) return 1; // Put invalid dates at the end
        if (!validB) return -1;
        
        return timestampA - timestampB; // Ascending order (oldest first, newest last)
      });
    });

    const isMessageFromUser = (message) => {
      if (!message) return false;
      
      const currentUser = authStore.state.user;
      if (!currentUser) return false;
      
      // Primary check: Use the is_from_user field added by the backend transformation
      // This is the most reliable since the backend does the heavy lifting
      if (message.is_from_user === true || message.is_from_user === false) {
        return message.is_from_user === true;
      }
      
      // Fallback checks if backend transformation didn't add the field
      // 1. Check if sender ID matches current user ID
      if (message.sender && currentUser.id && message.sender === currentUser.id) {
        return true;
      }
      
      // 2. Check if sender_id matches current user ID
      if (message.sender_id && currentUser.id && message.sender_id === currentUser.id) {
        return true;
      }
      
      // 3. Check from_jobseeker flag (added by backend)
      if (message.is_from_jobseeker === true || message.from_jobseeker === true) {
        return true;
      }
      
      // 4. Check sender_type for jobseeker (if current user is a jobseeker)
      if (currentUser.account_type === "jobseeker" && message.sender_type === "jobseeker") {
        return true;
      }
      
      // 5. If sender_account_type is explicitly "jobseeker" (set by backend)
      if (message.sender_account_type === "jobseeker") {
        return true;
      }
      
      // 6. Check if sender_account_type is not "employer" and current user is jobseeker
      if (
        currentUser.account_type === "jobseeker" &&
        message.sender_account_type &&
        message.sender_account_type !== "employer" &&
        message.sender_account_type !== "admin"
      ) {
        return true;
      }
      
      // 7. Heuristic: If sender_name doesn't match partner name and user is jobseeker,
      // it's likely from the user (since OLJ conversations are between jobseeker and employer)
      if (
        currentUser.account_type === "jobseeker" &&
        message.sender_name &&
        partnerName.value
      ) {
        const partnerNameLower = partnerName.value.toLowerCase().trim();
        const senderNameLower = message.sender_name.toLowerCase().trim();
        
        // If sender name doesn't match partner name, it's likely from the user
        if (senderNameLower !== partnerNameLower) {
          // Additional check: make sure it's not empty or generic
          if (senderNameLower && senderNameLower !== "employer" && senderNameLower !== "unknown") {
            return true;
          }
        }
      }
      
      // 8. Final heuristic based on owner.employer_type from OLJ payload
      if (currentUser.account_type === "jobseeker" && message.owner) {
        if (message.owner.employer_type) {
          return false;
        }
        return true;
      }
      
      return false;
    };

    const formatMessageDate = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const now = new Date();
      
      // Check if same day
      if (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      ) {
        return date.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        });
      }
      
      // Check if same year
      if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString([], {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      }
      
      return date.toLocaleDateString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    // Parse and format message body to handle JSON and HTML
    const formatMessageBody = (message) => {
      if (!message) return "No content";
      
      let body = message.body || message.message || message.content || "";
      
      // If body is empty, try other fields
      if (!body && message.data) {
        body = message.data;
      }
      
      // If body is a JSON string, try to parse it
      if (typeof body === "string" && body.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(body);
          // Extract body from parsed JSON (most common case)
          if (parsed.body) {
            body = parsed.body;
          } else if (parsed.message) {
            body = parsed.message;
          } else if (parsed.content) {
            body = parsed.content;
          } else {
            // Try to find the first string field that looks like content
            for (const key in parsed) {
              if (typeof parsed[key] === "string" && parsed[key].length > 10) {
                body = parsed[key];
                break;
              }
            }
          }
        } catch (e) {
          // If parsing fails, just use the original string
        }
      }
      
      // If body is an object, extract body field
      if (typeof body === "object" && body !== null) {
        if (body.body) {
          body = body.body;
        } else if (body.message) {
          body = body.message;
        } else if (body.content) {
          body = body.content;
        }
      }
      
      // Convert to string if not already
      if (typeof body !== "string") {
        body = String(body);
      }
      
      // Convert block elements to line breaks for better formatting
      body = body.replace(/<\/div>/gi, "<br>");
      body = body.replace(/<div[^>]*>/gi, "");
      body = body.replace(/<\/p>/gi, "<br>");
      body = body.replace(/<p[^>]*>/gi, "");

      // Normalize <br> tags
      body = body.replace(/<br\s*\/?>/gi, "<br>");

      // Clean up extra line breaks
      body = body.replace(/(<br>\s*){3,}/gi, "<br><br>");

      // Sanitize with DOMPurify to strip any dangerous tags/attributes (XSS protection)
      body = DOMPurify.sanitize(body, {
        ALLOWED_TAGS: ["br", "p", "b", "i", "u", "a", "ul", "ol", "li"],
        ALLOWED_ATTR: ["href", "target"],
        FORCE_BODY: true,
      });

      return body || "No content";
    };

    // Auto-scroll to bottom when messages load
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop =
            messagesContainer.value.scrollHeight;
        }
      });
    };

    // Watch for threadData changes and scroll to bottom
    watch(
      () => props.threadData,
      () => {
        scrollToBottom();
      },
      { deep: true }
    );

    onMounted(() => {
      scrollToBottom();
    });

    return {
      messagesContainer,
      partnerName,
      messages,
      isMessageFromUser,
      formatMessageDate,
      formatMessageBody,
    };
  },
};
</script>

<style scoped>
.olj-thread-container {
  height: 100%;
}

.messages-container {
  scrollbar-width: thin;
  scrollbar-color: hsl(var(--muted-foreground) / 0.4) hsl(var(--muted));
}

.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: hsl(var(--muted));
}

.messages-container::-webkit-scrollbar-thumb {
  background-color: hsl(var(--muted-foreground) / 0.4);
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background-color: hsl(var(--muted-foreground) / 0.6);
}

.message-item {
  display: flex;
  width: 100%;
  margin-bottom: 1rem;
}

.message-from-user {
  justify-content: flex-end;
  padding-left: 20%;
}

.message-from-partner {
  justify-content: flex-start;
  padding-right: 20%;
}

.message-bubble {
  word-wrap: break-word;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.message-from-user .message-bubble {
  margin-left: auto;
  margin-right: 0;
}

.message-from-partner .message-bubble {
  margin-left: 0;
  margin-right: auto;
}

.bg-primary {
  background-color: #4a6bfd;
}

.bg-primary-dark {
  background-color: #3a56d4;
}

.text-primary {
  color: #4a6bfd;
}
</style>

